import React, { useState } from 'react';
import {
  RcsComponent,
  RcsComponentType,
  COMPONENT_PALETTE,
  createDefaultComponent,
  RcsButton,
  RcsCard,
  RcsCardOrientation,
  RcsMediaHeight,
  RcsCardWidth,
} from './rcs-types';
import { SectionLabel, CharCount } from '../utiz';
import Input from '../../../input';
import {
  Type,
  Image,
  Video,
  LayoutGrid,
  Layers,
  MessageSquare,
  MousePointerClick,
  MapPin,
  Minus,
  Trash2,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const COMPONENT_ICONS: Record<RcsComponentType, React.ReactNode> = {
  text: <Type size={16} />,
  image: <Image size={16} />,
  video: <Video size={16} />,
  rich_card: <LayoutGrid size={16} />,
  carousel: <Layers size={16} />,
  suggested_replies: <MessageSquare size={16} />,
  cta_buttons: <MousePointerClick size={16} />,
  location: <MapPin size={16} />,
  divider: <Minus size={16} />,
};

interface RcsMessageBuilderProps {
  components: RcsComponent[];
  onChange: (components: RcsComponent[]) => void;
  readOnly?: boolean;
}

const PaletteItem: React.FC<{
  type: RcsComponentType;
  label: string;
  description: string;
  onAdd: () => void;
}> = ({ type, label, description, onAdd }) => {
  return (
    <button
      type="button"
      onClick={onAdd}
      className="flex items-center gap-3 w-full p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors text-left group"
    >
      <div className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
        {COMPONENT_ICONS[type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{description}</p>
      </div>
      <Plus size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-indigo-500 dark:group-hover:text-indigo-400" />
    </button>
  );
};

const ComponentItem: React.FC<{
  component: RcsComponent;
  onUpdate: (updates: Partial<RcsComponent>) => void;
  onRemove: () => void;
  readOnly?: boolean;
}> = ({ component, onUpdate, onRemove, readOnly }) => {
  const [expanded, setExpanded] = useState(true);
  const paletteItem = COMPONENT_PALETTE.find((p) => p.type === component.type);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-800">
        <div className="w-6 h-6 flex items-center justify-center bg-white dark:bg-gray-900 rounded text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
          {COMPONENT_ICONS[component.type]}
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1">{paletteItem?.label}</span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="p-3">
          <ComponentEditor
            component={component}
            onUpdate={onUpdate}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  );
};

const CarouselCardEditor: React.FC<{
  card: RcsCard;
  cardIndex: number;
  onUpdate: (updates: Partial<RcsCard>) => void;
  onRemove: () => void;
  readOnly?: boolean;
}> = ({ card, cardIndex, onUpdate, onRemove, readOnly }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-800">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Card {cardIndex + 1}</span>
        <span className="flex-1 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">
          {card.title || 'Untitled'}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {expanded && (
        <div className="p-3 space-y-2">
          <Input
            value={card.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ title: e.target.value })}
            placeholder="Card title"
            readOnly={readOnly}
          />
          <textarea
            value={card.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Card description"
            className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={2}
            readOnly={readOnly}
          />
          {!readOnly && (
            <label className="flex items-center justify-center gap-2 px-2 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors">
              <Image size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {card.mediaUrl ? 'Change media' : 'Upload media'}
              </span>
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onUpdate({ mediaFile: file, mediaUrl: URL.createObjectURL(file) });
                  }
                }}
              />
            </label>
          )}
          {card.mediaUrl && (
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img src={card.mediaUrl} alt="Preview" className="w-full h-16 object-cover" />
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Media Height</label>
            <div className="flex gap-1">
              {(['SHORT', 'MEDIUM', 'TALL'] as RcsMediaHeight[]).map((height) => (
                <button
                  key={height}
                  type="button"
                  onClick={() => !readOnly && onUpdate({ mediaHeight: height })}
                  className={`flex-1 px-2 py-1 text-[10px] font-medium rounded border transition-colors ${
                    card.mediaHeight === height
                      ? 'bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-400'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                  disabled={readOnly}
                >
                  {height.charAt(0) + height.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Buttons</label>
            <div className="space-y-1">
              {(card.buttons || []).map((btn, btnIdx) => (
                <div key={btn.id} className="flex items-center gap-1">
                  <Input
                    value={btn.text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newButtons = [...(card.buttons || [])];
                      newButtons[btnIdx] = { ...btn, text: e.target.value };
                      onUpdate({ buttons: newButtons });
                    }}
                    placeholder="Button text"
                    readOnly={readOnly}
                  />
                  <select
                    value={btn.action}
                    onChange={(e) => {
                      const newButtons = [...(card.buttons || [])];
                      newButtons[btnIdx] = { ...btn, action: e.target.value as RcsButton['action'] };
                      onUpdate({ buttons: newButtons });
                    }}
                    className="px-1 py-1 text-[10px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded focus:outline-none"
                    disabled={readOnly}
                  >
                    <option value="open_url">URL</option>
                    <option value="dial">Dial</option>
                  </select>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        const newButtons = (card.buttons || []).filter((_, i) => i !== btnIdx);
                        onUpdate({ buttons: newButtons });
                      }}
                      className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (card.buttons?.length || 0) < 4 && (
                <button
                  type="button"
                  onClick={() =>
                    onUpdate({
                      buttons: [
                        ...(card.buttons || []),
                        { id: `btn_${Date.now()}`, text: '', action: 'open_url', value: '' },
                      ],
                    })
                  }
                  className="flex items-center gap-1 text-[10px] text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  <Plus size={10} /> Add Button
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComponentEditor: React.FC<{
  component: RcsComponent;
  onUpdate: (updates: Partial<RcsComponent>) => void;
  readOnly?: boolean;
}> = ({ component, onUpdate, readOnly }) => {
  switch (component.type) {
    case 'text':
      return (
        <div>
          <textarea
            value={component.content || ''}
            onChange={(e) => onUpdate({ content: e.target.value })}
            placeholder="Enter your message text... Use {{1}}, {{2}} for variables"
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={3}
            maxLength={1024}
            readOnly={readOnly}
          />
          <CharCount current={component.content?.length || 0} max={1024} />
        </div>
      );

    case 'image':
    case 'video':
      return (
        <div className="space-y-2">
          {!readOnly && (
            <label className="flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors">
              <Image size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {component.mediaUrl ? 'Change' : 'Upload'} {component.type}
              </span>
              <input
                type="file"
                accept={component.type === 'image' ? 'image/*' : 'video/*'}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onUpdate({ mediaFile: file, mediaUrl: URL.createObjectURL(file) });
                  }
                }}
              />
            </label>
          )}
          {component.mediaUrl && (
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              {component.type === 'image' ? (
                <img src={component.mediaUrl} alt="Preview" className="w-full h-24 object-cover" />
              ) : (
                <video src={component.mediaUrl} className="w-full h-24 object-cover" controls />
              )}
            </div>
          )}
          <Input
            value={component.mediaCaption || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ mediaCaption: e.target.value })}
            placeholder="Caption (optional)"
            readOnly={readOnly}
          />
        </div>
      );

    case 'rich_card':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Card Orientation</label>
            <div className="flex gap-2">
              {(['VERTICAL', 'HORIZONTAL'] as RcsCardOrientation[]).map((orient) => (
                <button
                  key={orient}
                  type="button"
                  onClick={() => !readOnly && onUpdate({ orientation: orient })}
                  className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    component.orientation === orient
                      ? 'bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-400'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  disabled={readOnly}
                >
                  {orient === 'VERTICAL' ? 'Vertical (Image Top)' : 'Horizontal (Image Left)'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Media Height</label>
            <div className="flex gap-2">
              {(['SHORT', 'MEDIUM', 'TALL'] as RcsMediaHeight[]).map((height) => (
                <button
                  key={height}
                  type="button"
                  onClick={() => !readOnly && onUpdate({ mediaHeight: height })}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    component.mediaHeight === height
                      ? 'bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-400'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  disabled={readOnly}
                >
                  {height.charAt(0) + height.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <Input
            value={component.title || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ title: e.target.value })}
            placeholder="Card title"
            readOnly={readOnly}
          />
          <textarea
            value={component.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Card description"
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={2}
            readOnly={readOnly}
          />
          {!readOnly && (
            <label className="flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors">
              <Image size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {component.mediaUrl ? 'Change media' : 'Upload media'}
              </span>
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onUpdate({ mediaFile: file, mediaUrl: URL.createObjectURL(file) });
                  }
                }}
              />
            </label>
          )}
          {component.mediaUrl && (
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img src={component.mediaUrl} alt="Preview" className="w-full h-20 object-cover" />
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Buttons (Max 4)</label>
            <div className="space-y-2">
              {(component.buttons || []).map((btn, idx) => (
                <div key={btn.id} className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={btn.text}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newButtons = [...(component.buttons || [])];
                        newButtons[idx] = { ...btn, text: e.target.value };
                        onUpdate({ buttons: newButtons });
                      }}
                      placeholder="Button text"
                      readOnly={readOnly}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          const newButtons = (component.buttons || []).filter((_, i) => i !== idx);
                          onUpdate({ buttons: newButtons });
                        }}
                        className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={btn.action}
                      onChange={(e) => {
                        const newButtons = [...(component.buttons || [])];
                        newButtons[idx] = { ...btn, action: e.target.value as RcsButton['action'] };
                        onUpdate({ buttons: newButtons });
                      }}
                      className="px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={readOnly}
                    >
                      <option value="open_url">Open URL</option>
                      <option value="dial">Dial Phone</option>
                      <option value="location">View Location</option>
                      <option value="calendar">Add to Calendar</option>
                    </select>
                    <Input
                      value={btn.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newButtons = [...(component.buttons || [])];
                        newButtons[idx] = { ...btn, value: e.target.value };
                        onUpdate({ buttons: newButtons });
                      }}
                      placeholder={btn.action === 'dial' ? 'Phone number' : 'URL'}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              ))}
              {!readOnly && (component.buttons?.length || 0) < 4 && (
                <button
                  type="button"
                  onClick={() =>
                    onUpdate({
                      buttons: [
                        ...(component.buttons || []),
                        { id: `btn_${Date.now()}`, text: '', action: 'open_url', value: '' },
                      ],
                    })
                  }
                  className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  <Plus size={12} /> Add Button
                </button>
              )}
            </div>
          </div>
        </div>
      );

    case 'suggested_replies':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Intro Text</label>
            <Input
              value={component.content || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ content: e.target.value })}
              placeholder="e.g., Interested?"
              readOnly={readOnly}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Replies</label>
            <div className="space-y-2">
              {(component.suggestedReplies || []).map((reply, idx) => (
                <div key={reply.id} className="flex items-center gap-2">
                  <Input
                    value={reply.text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newReplies = [...(component.suggestedReplies || [])];
                      newReplies[idx] = { ...reply, text: e.target.value };
                      onUpdate({ suggestedReplies: newReplies });
                    }}
                    placeholder="Reply text (e.g., Yes)"
                    readOnly={readOnly}
                  />
                  {!readOnly && (component.suggestedReplies?.length || 0) > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newReplies = (component.suggestedReplies || []).filter((_, i) => i !== idx);
                        onUpdate({ suggestedReplies: newReplies });
                      }}
                      className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (component.suggestedReplies?.length || 0) < 11 && (
                <button
                  type="button"
                  onClick={() =>
                    onUpdate({
                      suggestedReplies: [
                        ...(component.suggestedReplies || []),
                        { id: `reply_${Date.now()}`, text: '', postbackData: '' },
                      ],
                    })
                  }
                  className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  <Plus size={14} /> Add Reply
                </button>
              )}
            </div>
          </div>
        </div>
      );

    case 'cta_buttons':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Intro Text</label>
            <Input
              value={component.ctaText || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ ctaText: e.target.value })}
              placeholder="e.g., Need help?"
              readOnly={readOnly}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Actions</label>
            {(component.buttons || []).map((btn, idx) => (
              <div key={btn.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2 mb-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={btn.text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newButtons = [...(component.buttons || [])];
                      newButtons[idx] = { ...btn, text: e.target.value };
                      onUpdate({ buttons: newButtons });
                    }}
                    placeholder="Button text (e.g., Call us)"
                    readOnly={readOnly}
                  />
                  {!readOnly && (component.buttons?.length || 0) > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newButtons = (component.buttons || []).filter((_, i) => i !== idx);
                        onUpdate({ buttons: newButtons });
                      }}
                      className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={btn.action}
                    onChange={(e) => {
                      const newButtons = [...(component.buttons || [])];
                      newButtons[idx] = { ...btn, action: e.target.value as RcsButton['action'] };
                      onUpdate({ buttons: newButtons });
                    }}
                    className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={readOnly}
                  >
                    <option value="dial">Phone Number</option>
                    <option value="open_url">Open URL</option>
                    <option value="location">View Location</option>
                    <option value="calendar">Add to Calendar</option>
                  </select>
                  <Input
                    value={btn.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newButtons = [...(component.buttons || [])];
                      newButtons[idx] = { ...btn, value: e.target.value };
                      onUpdate({ buttons: newButtons });
                    }}
                    placeholder={btn.action === 'dial' ? '+15551234567' : 'URL'}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            ))}
            {!readOnly && (component.buttons?.length || 0) < 4 && (
              <button
                type="button"
                onClick={() =>
                  onUpdate({
                    buttons: [
                      ...(component.buttons || []),
                      { id: `btn_${Date.now()}`, text: '', action: 'dial', value: '' },
                    ],
                  })
                }
                className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                <Plus size={14} /> Add Action
              </button>
            )}
          </div>
        </div>
      );

    case 'location':
      return (
        <div className="space-y-2">
          <Input
            value={component.locationLabel || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ locationLabel: e.target.value })}
            placeholder="Location label (e.g., Our Store)"
            readOnly={readOnly}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={component.latitude?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ latitude: parseFloat(e.target.value) || undefined })}
              placeholder="Latitude (e.g., 6.5244)"
              type="number"
              readOnly={readOnly}
            />
            <Input
              value={component.longitude?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ longitude: parseFloat(e.target.value) || undefined })}
              placeholder="Longitude (e.g., 3.3792)"
              type="number"
              readOnly={readOnly}
            />
          </div>
          <Input
            value={component.locationText || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ locationText: e.target.value })}
            placeholder="Description (e.g., Visit us in Lagos)"
            readOnly={readOnly}
          />
        </div>
      );

    case 'divider':
      return (
        <div className="flex items-center justify-center py-2">
          <div className="w-full border-t border-gray-300 dark:border-gray-600 border-dashed" />
        </div>
      );

    case 'carousel':
      const cards = component.cards || [];
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">
              Intro Text <span className="text-[#7B7B7B] font-semibold dark:text-gray-500 font-medium">(optional)</span>
            </label>
            <textarea
              value={component.carouselText || ''}
              onChange={(e) => onUpdate({ carouselText: e.target.value })}
              placeholder="e.g., Hello {{1}}, check out what's new!"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={2}
              readOnly={readOnly}
            />
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              Use {'{{1}}'}, {'{{2}}'} for variables
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Card Width</label>
            <div className="flex gap-2">
              {(['SMALL', 'MEDIUM'] as RcsCardWidth[]).map((width) => (
                <button
                  key={width}
                  type="button"
                  onClick={() => !readOnly && onUpdate({ cardWidth: width })}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    component.cardWidth === width
                      ? 'bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-400'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  disabled={readOnly}
                >
                  {width.charAt(0) + width.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">
              Cards ({cards.length}/10)
            </label>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {cards.map((card, idx) => (
                <CarouselCardEditor
                  key={card.id}
                  card={card}
                  cardIndex={idx}
                  onUpdate={(updates) => {
                    const newCards = [...cards];
                    newCards[idx] = { ...card, ...updates };
                    onUpdate({ cards: newCards });
                  }}
                  onRemove={() => {
                    const newCards = cards.filter((_, i) => i !== idx);
                    onUpdate({ cards: newCards });
                  }}
                  readOnly={readOnly}
                />
              ))}
            </div>
            {!readOnly && cards.length < 10 && (
              <button
                type="button"
                onClick={() => {
                  const newCard: RcsCard = {
                    id: `card_${Date.now()}`,
                    title: '',
                    description: '',
                    mediaUrl: '',
                    mediaHeight: 'MEDIUM',
                    buttons: [],
                  };
                  onUpdate({ cards: [...cards, newCard] });
                }}
                className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300 mt-2"
              >
                <Plus size={12} /> Add Card
              </button>
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
};

export const RcsMessageBuilder: React.FC<RcsMessageBuilderProps> = ({
  components,
  onChange,
  readOnly,
}) => {
  const handleAddComponent = (type: RcsComponentType) => {
    const newComponent = createDefaultComponent(type);
    onChange([...components, newComponent]);
  };

  const handleUpdateComponent = (id: string, updates: Partial<RcsComponent>) => {
    onChange(components.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const handleRemoveComponent = (id: string) => {
    onChange(components.filter((c) => c.id !== id));
  };

  return (
    <div className="flex gap-4">
      <div className="w-56 flex-shrink-0">
        <SectionLabel label="Components" />
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-3">Click to add components</p>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {COMPONENT_PALETTE.map((item) => (
            <PaletteItem
              key={item.type}
              type={item.type}
              label={item.label}
              description={item.description}
              onAdd={() => handleAddComponent(item.type)}
            />
          ))}
        </div>
      </div>

      <div className="flex-1">
        <SectionLabel label="Message Components" />
        <div className="min-h-48 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-3 bg-gray-50/50 dark:bg-gray-800/50">
          {components.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-[#7B7B7B] font-semibold dark:text-gray-500">
              <Layers size={32} className="mb-2 opacity-50" />
              <p className="text-sm">Click components to add them here</p>
            </div>
          ) : (
            components.map((component) => (
              <ComponentItem
                key={component.id}
                component={component}
                onUpdate={(updates: Partial<RcsComponent>) => handleUpdateComponent(component.id, updates)}
                onRemove={() => handleRemoveComponent(component.id)}
                readOnly={readOnly}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
